import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Package, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { ItemWarehouseData } from '@/types/database/procurement-item-warehouse';

interface ItemWarehouseReceivingFormProps {
  itemWarehouseData: ItemWarehouseData[];
  setItemWarehouseData: React.Dispatch<React.SetStateAction<ItemWarehouseData[]>>;
}

const ItemWarehouseReceivingForm: React.FC<ItemWarehouseReceivingFormProps> = ({
  itemWarehouseData,
  setItemWarehouseData
}) => {
  const updateItemData = (itemId: string, field: keyof ItemWarehouseData, value: any) => {
    setItemWarehouseData(prev => 
      prev.map(item => {
        if (item.itemId !== itemId) return item;
        
        const updated = { ...item, [field]: value };
        
        // Auto-calculate net payable when received quantity changes (floor at 0)
        if (field === 'receivedQuantityKg') {
          updated.netPayableKg = Math.max(0, (value as number) - (item.compensationKg || 0));
        }
        
        return updated;
      })
    );
  };

  const calculateTotals = () => {
    return itemWarehouseData.reduce((acc, item) => ({
      totalExpected: acc.totalExpected + (item.expectedQuantity || 0),
      totalReceived: acc.totalReceived + (item.receivedQuantityKg || 0),
      totalCompensation: acc.totalCompensation + (item.compensationKg || 0),
      totalNetPayable: acc.totalNetPayable + (item.netPayableKg || 0),
      rejectedCount: acc.rejectedCount + ((item as any).isRejected ? 1 : 0)
    }), { totalExpected: 0, totalReceived: 0, totalCompensation: 0, totalNetPayable: 0, rejectedCount: 0 });
  };

  const totals = calculateTotals();
  const hasVariance = Math.abs(totals.totalExpected - totals.totalReceived) > 0.5;

  // Separate rejected and non-rejected items
  const activeItems = itemWarehouseData.filter(item => !(item as any).isRejected);
  const rejectedItems = itemWarehouseData.filter(item => (item as any).isRejected);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <Package className="h-4 w-4" />
          Per-Item Warehouse Receiving ({itemWarehouseData.length} items)
        </h3>
        {totals.rejectedCount > 0 && (
          <Badge variant="secondary" className="text-muted-foreground">
            {totals.rejectedCount} item(s) rejected at quality
          </Badge>
        )}
      </div>

      {/* Rejected Items Section */}
      {rejectedItems.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <XCircle className="h-4 w-4 text-destructive" />
            Rejected at Quality (No receiving needed)
          </h4>
          {rejectedItems.map((itemData, index) => (
            <Card key={itemData.itemId} className="border-l-4 border-l-destructive bg-destructive/5">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">#{activeItems.length + index + 1}</span>
                    <span className="line-through text-muted-foreground">{itemData.productName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      Rejected at Quality
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {itemData.expectedQuantity} KG → 0 KG payable
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Active Items Section */}
      <div className="space-y-4">
        {activeItems.map((itemData, index) => {
          const receivedDiff = (itemData.receivedQuantityKg || 0) - itemData.expectedQuantity;
          const hasItemVariance = Math.abs(receivedDiff) > 0.5;
          
          return (
            <Card key={itemData.itemId} className="border-l-4 border-l-primary/30">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">#{index + 1}</span>
                    {itemData.productName}
                  </span>
                  <div className="flex items-center gap-2">
                    {itemData.qualityGrade && (
                      <Badge variant="secondary">Grade: {itemData.qualityGrade}</Badge>
                    )}
                    <Badge variant="outline">Expected: {itemData.expectedQuantity} KG</Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {/* Quality info from inspection - read only */}
                {itemData.compensationKg > 0 && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                    Quality compensation: {itemData.compensationKg} KG (from quality inspection)
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Received Quantity (KG)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      className="h-9"
                      value={itemData.receivedQuantityKg || ''}
                      onChange={(e) => updateItemData(itemData.itemId, 'receivedQuantityKg', parseFloat(e.target.value) || 0)}
                      placeholder={`${itemData.expectedQuantity}`}
                    />
                    {hasItemVariance && (
                      <p className={`text-xs ${receivedDiff < 0 ? 'text-destructive' : 'text-green-600'}`}>
                        {receivedDiff > 0 ? '+' : ''}{receivedDiff.toFixed(1)} KG vs expected
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Compensation (KG)</Label>
                    <div className="h-9 px-3 flex items-center bg-muted rounded-md text-sm">
                      {(itemData.compensationKg || 0).toFixed(1)} KG
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Net Payable (KG)</Label>
                    <div className="h-9 px-3 flex items-center bg-green-50 border border-green-200 rounded-md text-sm font-medium text-green-700">
                      {(itemData.netPayableKg || 0).toFixed(1)} KG
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Warehouse Notes</Label>
                  <Textarea
                    value={itemData.notes}
                    onChange={(e) => updateItemData(itemData.itemId, 'notes', e.target.value)}
                    placeholder="Receiving notes for this item..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Totals Summary */}
      <Card className="bg-muted/50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="font-medium flex items-center gap-2">
              {hasVariance ? (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              ) : (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
              Totals Summary
            </span>
            <div className="flex gap-4 text-sm flex-wrap">
              <span>
                <span className="text-muted-foreground">Expected:</span>{' '}
                <strong>{totals.totalExpected.toFixed(1)} KG</strong>
              </span>
              <span>
                <span className="text-muted-foreground">Received:</span>{' '}
                <strong className={hasVariance ? 'text-amber-600' : ''}>
                  {totals.totalReceived.toFixed(1)} KG
                </strong>
              </span>
              <span>
                <span className="text-muted-foreground">Compensation:</span>{' '}
                <strong className="text-destructive">{totals.totalCompensation.toFixed(1)} KG</strong>
              </span>
              <span>
                <span className="text-muted-foreground">Net Payable:</span>{' '}
                <strong className="text-green-600">{totals.totalNetPayable.toFixed(1)} KG</strong>
              </span>
            </div>
          </div>
          {hasVariance && (
            <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span>
                Variance of {Math.abs(totals.totalExpected - totals.totalReceived).toFixed(1)} KG between expected and received
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ItemWarehouseReceivingForm;
